import {createAsyncThunk} from '@reduxjs/toolkit';
import Events, {FundStatus} from 'src/logging/Events';
import {alertError} from 'src/components/helpers/AlertHelper';
import {hideSpinner, showSpinner} from 'src/redux/slices/screenSlice';
import Localized from 'src/constants/AppStrings';
import {AppDispatch, RootState} from '../../store';
import Settings from 'src/Settings';
import Util from 'src/Util';
import AppApi from 'src/api/AppApi';
import {handleNextAction, initStripe} from 'src/nativeModules/Stripe';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import {PaymentCredentials} from '../../../models/PaymentCredentials';
import Logger from 'src/logging/Logger';
import {generateErrorMessage} from '../../../logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import AccountConstants from '../../../constants/AccountConstants';

const PAYROLL_LIMIT_ERROR = 'insufficient-payroll-balance';
const DUPLICATE_TRANSACTION_REGEX =
  /Transaction was rejected because it is a duplicate\. Subject '(\d+)'/;

export function addFundsFailed(error: any) {
  let errorMessage = Localized.Errors.failed_to_add_funds;

  if (error === PAYROLL_LIMIT_ERROR) {
    // Payroll balance limit reached
    errorMessage = Localized.Errors.reached_payroll_deduct_limit;
  } else if (typeof error === 'string') {
    const match = (error ?? '').match(DUPLICATE_TRANSACTION_REGEX);
    if (match) {
      const duplicateId = match[1];
      errorMessage = `${Localized.Labels.duplicate_transaction_error} '${duplicateId}'.\n${Localized.Errors.please_try_again_shortly}`;
    }
  }

  alertError(errorMessage);
}

async function addFundsSuccess({
  amount,
  cardToken,
  paymentCredentials,
  dispatch,
}: {
  amount: number;
  cardToken: string;
  paymentCredentials: PaymentCredentials;
  dispatch: AppDispatch;
  accountId: string;
}) {
  Events.AddFunds.trackEvent(
    FundStatus.Success,
    amount,
    cardToken,
    paymentCredentials.type,
  );
  // TODO will remove
  const data = await ActionsFactory.getAccountActions().getBalance(
    AccountStore.getAccountId(),
    true,
  );
  if (!data) {
    throw new Error(AccountConstants.GET_BALANCE_ERROR);
  }
}

export interface IAddFundsCardParams {
  amount: number;
  tokenId: string;
  addFundSuccess?: () => void;
  addFundFailedCallback?: (error?: any) => void;
}

export const addFundsCard = createAsyncThunk<
  void,
  IAddFundsCardParams,
  {dispatch: AppDispatch; state: RootState}
>(
  'funding/addFundsCard',
  async (
    {amount, tokenId, addFundSuccess, addFundFailedCallback},
    {dispatch, getState},
  ) => {
    dispatch(
      showSpinner({
        spinnerText: `${Localized.Labels.adding_funds}...`,
      }),
    );

    try {
      const {paymentCredentials, account, isHomeLocationSma} =
        getState().account;
      const balanceId = isHomeLocationSma
        ? account?.smaBalance?.id
        : account?.defaultBalance?.id;
      const processor = Settings.processors[paymentCredentials.type];

      const result = (await AppApi.addFundsToken(
        account.id,
        balanceId,
        tokenId,
        amount,
        Util.getCurrentDate(),
      )) as {
        accountId: string;
        message: string;
        clientSecret?: string;
        transactionId: string;
        status: string;
      };
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'AddFundsToken',
        JSON.stringify({
          accountId: account.id,
          balanceId,
          tokenId,
          amount,
          transDate: Util.getCurrentDate(),
        }),
        JSON.stringify(result),
      );

      /** if clientsecret is returned */
      if (processor === Settings.processors.stripe && result?.clientSecret) {
        await initStripe({
          publishableKey: paymentCredentials.key,
        });

        const {error, paymentIntent} = await handleNextAction(
          result?.clientSecret,
        );

        if (error) {
          CrashlyticsEvents.log(
            'Exception',
            'FundingService:AddFunds',
            generateErrorMessage(error),
          );
          Events.Error.trackEvent(
            'Exception',
            'FundingService:AddFunds',
            generateErrorMessage(error),
          );
          addFundFailedCallback ? addFundFailedCallback() : addFundsFailed();
        } else {
          const confirmPaymentRes = await AppApi.confirmPaymentIntent(
            account.id,
            balanceId,
            paymentIntent.id,
            result.transactionId,
            tokenId,
          );
          Logger.Log.LogAPIEvent(
            'PaymentAPI',
            'ConfirmPaymentIntent',
            JSON.stringify({
              transId: result.transactionId,
              accountId: account.id,
            }),
            JSON.stringify(confirmPaymentRes),
          );
          await addFundsSuccess({
            amount,
            cardToken: tokenId,
            paymentCredentials,
            dispatch,
            accountId: account.id,
          });
          addFundSuccess && addFundSuccess();
        }
      } else if (result && result.accountId) {
        await addFundsSuccess({
          amount,
          cardToken: tokenId,
          paymentCredentials,
          dispatch,
          accountId: account.id,
        });
        addFundSuccess && addFundSuccess();
      } else {
        CrashlyticsEvents.log(
          'Exception',
          'FundingService:AddFunds',
          generateErrorMessage(result.message),
        );
        Events.Error.trackEvent(
          'Exception',
          'FundingService:AddFunds',
          generateErrorMessage(result.message),
        );
        throw new Error(result.message);
      }
    } catch (exception) {
      let errorMessage = '';
      try {
        const parsed = JSON.parse(exception.message);
        errorMessage = parsed.message;
      } catch (_) {
        errorMessage = '';
      }

      CrashlyticsEvents.log(
        'Exception',
        'FundingService:AddFunds',
        generateErrorMessage(exception),
      );
      Events.Error.trackEvent(
        'Exception',
        'FundingService:AddFunds',
        generateErrorMessage(exception),
      );
      addFundFailedCallback
        ? addFundFailedCallback()
        : addFundsFailed(errorMessage);
    } finally {
      dispatch(hideSpinner());
    }
  },
);
